import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiMail, FiPhone, FiMapPin, FiInstagram, FiLinkedin, FiFacebook, FiMessageCircle } = FiIcons;

const Footer = () => {
  const quickLinks = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Benefits', href: '#benefits' },
    { label: 'About Sanna', href: '#about' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Take Quiz', href: 'https://www.wethebillions.com/quiz' },
    { label: 'Book Consultation', href: '#contact' }
  ];

  const legalLinks = [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Accessibility', href: '/accessibility' }
  ];

  const socialLinks = [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/sanna-darwish-m-s-ccc-slp-b2a3491a8',
      icon: FiLinkedin
    },
    {
      name: 'Facebook', 
      href: 'https://Facebook.com/337098602826472',
      icon: FiFacebook
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/wethebillions',
      icon: FiInstagram
    },
    {
      name: 'WhatsApp',
      href: 'https://wa.me/+12026560054',
      icon: FiMessageCircle
    }
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.open(href, '_blank', 'noopener noreferrer');
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753416293845-4.png"
                alt="We The Billions Brain Icon"
                className="w-12 h-12"
              />
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170072528-v%20lg%20We%20The%20Billions%20logo.png"
                alt="We The Billions Logo"
                className="h-8 w-auto"
              />
            </div>
            <p className="text-xl font-playfair font-semibold text-accent-gold mb-4">
              86 billion neurons. 1 unique YOU.
            </p>
            <p className="text-gray-300 font-raleway leading-relaxed mb-6">
              Executive function coaching that transforms how you sleep, think, and thrive.
            </p>
            <div className="flex items-center gap-2 text-gray-300">
              <span>Made with</span>
              <SafeIcon icon={FiHeart} className="w-5 h-5 text-red-500" />
              <span>for every unique brain</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-playfair font-bold mb-6">Quick Links</h3>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(link.href)}
                  className="block text-gray-300 hover:text-accent-gold transition-colors duration-200 font-raleway"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-playfair font-bold mb-6">Contact</h3>
            <div className="space-y-4">
              <a
                href="mailto:hello@wethebillions.com"
                className="flex items-center gap-3 text-gray-300 hover:text-accent-gold transition-colors duration-200"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
                <span className="font-raleway">hello@wethebillions.com</span>
              </a>
              <a
                href="tel:+12026560054"
                className="flex items-center gap-3 text-gray-300 hover:text-accent-gold transition-colors duration-200"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span className="font-raleway">(202) 656-0054</span>
              </a>
              <div className="flex items-center gap-3 text-gray-300">
                <SafeIcon icon={FiMapPin} className="w-5 h-5" />
                <span className="font-raleway">Virtual (globally) & In-Person Sessions (MD & VA)</span>
              </div>
              <div className="pt-2">
                <a
                  href="https://www.wethebillions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-gold hover:text-accent-yellow transition-colors duration-200 font-raleway"
                >
                  www.wethebillions.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Social & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-playfair font-bold mb-6">Follow Us</h3>
            <div className="flex gap-4 mb-8">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-accent-magenta transition-colors duration-200"
                  title={social.name}
                >
                  <SafeIcon icon={social.icon} className="w-5 h-5 text-white" />
                </a>
              ))}
            </div>
            <div className="space-y-3">
              {legalLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="block text-gray-400 hover:text-gray-300 transition-colors duration-200 font-raleway text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 mt-12 pt-8 text-center"
        >
          <p className="text-gray-400 font-raleway">
            © {new Date().getFullYear()} We The Billions. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;